import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

function getConfigData(t) {
  return {
    tops: [
      { key: 'bimini_open', name: t('configurator.options.biminiTop'), price: 950, desc: t('configurator.options.biminiTopDesc') },
      { key: 'bimini_folded', name: t('configurator.options.biminiTop'), price: 950, desc: t('configurator.options.biminiTopDesc') },
    ],
    engines: [
      { key: 'outboard', name: t('configurator.options.outboard'), price: 4500, desc: t('configurator.options.outboardDesc') },
    ],
    seating: [
      { key: 'fore_seat', name: t('configurator.options.foreSeat'), price: 780, desc: t('configurator.options.foreSeatDesc') },
      { key: 'fore_sunbed', name: t('configurator.options.foreSunbed'), price: 750, desc: t('configurator.options.foreSunbedDesc') },
      { key: 'aft_sunbed', name: t('configurator.options.aftSunbed'), price: 800, desc: t('configurator.options.aftSunbedDesc') },
    ],
    extras: [
      { key: 'foreCushion', name: t('configurator.options.foreCushion'), price: 290, desc: t('configurator.options.foreCushionDesc') },
      { key: 'foreTableUp', name: t('configurator.options.foreTableUp'), price: 430, desc: t('configurator.options.foreTableUpDesc') },
      { key: 'foreTableDown', name: t('configurator.options.foreTableDown'), price: 700, desc: t('configurator.options.foreTableDownDesc') },
      { key: 'gps', name: t('configurator.options.gps'), price: 780, desc: t('configurator.options.gpsDesc') },
      { key: 'windshield', name: t('configurator.options.windshield'), price: 950, desc: t('configurator.options.windshieldDesc') },
      { key: 'multimedia', name: t('configurator.options.multimedia'), price: 780, desc: t('configurator.options.multimediaDesc') },
      { key: 'sideRails', name: t('configurator.options.sideRails'), price: 780, desc: t('configurator.options.sideRailsDesc') },
      { key: 'evaDecking', name: t('configurator.options.evaDecking'), price: 1500, desc: t('configurator.options.evaDeckingDesc') },
    ],
  };
}

function OptionCard({ name, price, desc, selected, onClick, isCheckbox, includedLabel }) {
  return (
    <div className={`config-option ${selected ? 'config-option--selected' : ''}`} onClick={onClick}>
      <div className="config-option__radio">
        {isCheckbox ? (
          <div className={`config-option__checkbox ${selected ? 'config-option__checkbox--checked' : ''}`}>
            {selected && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
        ) : (
          <div className={`config-option__dot ${selected ? 'config-option__dot--selected' : ''}`}>
            {selected && <div className="config-option__dot-inner" />}
          </div>
        )}
      </div>
      <div className="config-option__info">
        <span className="config-option__name">{name}</span>
        {desc && <span className="config-option__desc">{desc}</span>}
      </div>
      <span className="config-option__price">
        {price > 0 ? `+${price.toLocaleString('en-US')}€` : includedLabel}
      </span>
    </div>
  );
}

const COLOR_PALETTE = [
  { hex: '#5A7A8A', name: 'Steel Blue' },
  { hex: '#1B3A4B', name: 'Navy' },
  { hex: '#2C5F2D', name: 'Forest Green' },
  { hex: '#8B0000', name: 'Deep Red' },
  { hex: '#F5F5F5', name: 'Arctic White' },
  { hex: '#2B2B2B', name: 'Carbon Black' },
  { hex: '#C4A35A', name: 'Sand Gold' },
  { hex: '#E8712B', name: 'Unimar Orange' },
];

const COLOR_PARTS = [
  { key: 'hull', label: 'Hull' },
  { key: 'deck', label: 'Deck' },
  { key: 'upholstery', label: 'Upholstery' },
  { key: 'bimini', label: 'Bimini Top' },
  { key: 'bumper', label: 'Bumper' },
  { key: 'console', label: 'Console' },
];

function useAnimatedPrice(target) {
  const [displayed, setDisplayed] = useState(target);
  const rafRef = useRef(null);
  const currentRef = useRef(target);

  useEffect(() => {
    if (currentRef.current === target) return;
    const start = currentRef.current;
    const diff = target - start;
    const duration = 400;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(start + diff * eased);
      currentRef.current = value;
      setDisplayed(value);
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
      else currentRef.current = target;
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target]);

  return displayed;
}

function ConfigPanel({ activeCategory, selections, onSelect, onToggleExtra, onNext, onFinish, totalPrice, isLastStep }) {
  const { t } = useTranslation();
  const animatedPrice = useAnimatedPrice(totalPrice);
  const configData = getConfigData(t);

  const categoryTitles = {
    tops: t('configurator.panelTitles.tops'),
    engines: t('configurator.panelTitles.engines'),
    seating: t('configurator.panelTitles.seating'),
    extras: t('configurator.panelTitles.extras'),
    colors: t('configurator.panelTitles.colors'),
  };

  const includedLabel = t('configurator.options.included');

  return (
    <div className="config-panel">
      <div className="config-panel__header">
        <h3 className="config-panel__title">{categoryTitles[activeCategory]}</h3>
      </div>

      <div className="config-panel__body">
        {/* TOPS */}
        {activeCategory === 'tops' && (
          <>
            <OptionCard
              name={t('configurator.options.none')}
              price={0}
              desc={t('configurator.options.noTop')}
              selected={selections.top === null}
              onClick={() => onSelect('top', null)}
              includedLabel={includedLabel}
            />
            <div
              className={`config-option config-option--group ${selections.top === 'bimini_open' || selections.top === 'bimini_folded' ? 'config-option--selected' : ''}`}
              onClick={() => { if (!selections.top || selections.top === null) onSelect('top', 'bimini_open'); }}
            >
              <div className="config-option__radio">
                <div className={`config-option__dot ${selections.top === 'bimini_open' || selections.top === 'bimini_folded' ? 'config-option__dot--selected' : ''}`}>
                  {(selections.top === 'bimini_open' || selections.top === 'bimini_folded') && <div className="config-option__dot-inner" />}
                </div>
              </div>
              <div className="config-option__info">
                <span className="config-option__name">{t('configurator.options.biminiTop')}</span>
                <span className="config-option__desc">{t('configurator.options.biminiTopDesc')}</span>
                {(selections.top === 'bimini_open' || selections.top === 'bimini_folded') && (
                  <div className="config-option__position-toggle" onClick={e => e.stopPropagation()}>
                    <button
                      className={`config-option__pos-btn ${selections.top === 'bimini_open' ? 'config-option__pos-btn--active' : ''}`}
                      onClick={() => onSelect('top', 'bimini_open')}
                    >{t('configurator.options.open')}</button>
                    <button
                      className={`config-option__pos-btn ${selections.top === 'bimini_folded' ? 'config-option__pos-btn--active' : ''}`}
                      onClick={() => onSelect('top', 'bimini_folded')}
                    >{t('configurator.options.folded')}</button>
                  </div>
                )}
              </div>
              <span className="config-option__price">+950€</span>
            </div>
          </>
        )}

        {/* ENGINES */}
        {activeCategory === 'engines' && (
          <>
            <OptionCard
              name={t('configurator.options.none')}
              price={0}
              desc={t('configurator.options.noEngine')}
              selected={selections.engine === null}
              onClick={() => onSelect('engine', null)}
              includedLabel={includedLabel}
            />
            {configData.engines.map((item) => (
              <OptionCard
                key={item.key}
                name={item.name}
                price={item.price}
                desc={item.desc}
                selected={selections.engine === item.key}
                onClick={() => onSelect('engine', item.key)}
                includedLabel={includedLabel}
              />
            ))}
          </>
        )}

        {/* SEATING */}
        {activeCategory === 'seating' && (
          <>
            {configData.seating.map((item) => (
              <OptionCard
                key={item.key}
                name={item.name}
                price={item.price}
                desc={item.desc}
                selected={selections.seating?.[item.key] || false}
                onClick={() => onToggleExtra(`seating_${item.key}`)}
                isCheckbox
                includedLabel={includedLabel}
              />
            ))}
          </>
        )}

        {/* EXTRAS */}
        {activeCategory === 'extras' && (
          <>
            {configData.extras.map((item) => (
              <OptionCard
                key={item.key}
                name={item.name}
                price={item.price}
                desc={item.desc}
                selected={selections.extras?.[item.key] || false}
                onClick={() => onToggleExtra(item.key)}
                isCheckbox
                includedLabel={includedLabel}
              />
            ))}
          </>
        )}

        {/* COLORS */}
        {activeCategory === 'colors' && (
          <div className="config-colors-parts">
            {COLOR_PARTS.map((part) => (
              <div key={part.key} className="config-color-part">
                <h4 className="config-color-part__label">{part.label}</h4>
                <div className="config-color-part__swatches">
                  {COLOR_PALETTE.map((color) => (
                    <div
                      key={color.hex}
                      className={`config-color-dot ${selections.colors?.[part.key] === color.hex ? 'config-color-dot--selected' : ''}`}
                      style={{ background: color.hex }}
                      title={color.name}
                      onClick={() => {
                        const newColors = { ...selections.colors, [part.key]: color.hex };
                        onSelect('colors', newColors);
                      }}
                    />
                  ))}
                  {selections.colors?.[part.key] && (
                    <button
                      className="config-color-dot config-color-dot--reset"
                      title="Reset"
                      onClick={() => {
                        const newColors = { ...selections.colors, [part.key]: null };
                        onSelect('colors', newColors);
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom: Price + Actions */}
      <div className="config-panel__footer">
        <div className="config-panel__total">
          <span className="config-panel__total-label">{t('configurator.estimatedTotal')}</span>
          <span className="config-panel__total-price">{animatedPrice.toLocaleString('en-US')} &euro;</span>
        </div>
        <div className="config-panel__actions">
          <button className="config-panel__next" onClick={onNext}>
            {isLastStep ? t('configurator.finishBuildBtn') : t('configurator.nextStep')}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {!isLastStep && (
            <button className="config-panel__finish" onClick={onFinish}>
              {t('configurator.skipToSummary')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConfigPanel;
